import {
  Button,
  ButtonGroup,
  Container,
  Display6,
  Flex,
  SearchInput,
} from '@sainsburys-tech/fable'
import { SainsburysOrange } from '@sainsburys-tech/images'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { getUserStore, setUserStore } from '../../utils/localStore'
import { getStores } from '../api/storeApi'
import { useApiClient } from '../AppContext/appContext'
import { IStore } from '../types/IStore'
import Loading from './Loading'

const StoreSetting = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [confirmEnabled, setConfirmEnabled] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [stores, setStores] = useState<IStore[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [tempStore, setTempStore] = useState<IStore>({
    storeId: 0,
    storeCode: '',
    storeName: '',
  })

  const apiClient = useApiClient()

  useEffect(() => {
    if (location.search !== '?reset=true') {
      const store = getUserStore()
      if (store.storeId !== 0) {
        setIsLoading(false)
        navigate('/plannerselection')
        return
      }
    }

    getStores(apiClient)
      .then((res) => setStores(res))
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false))
  }, [apiClient, location.search, navigate])

  useEffect(() => {
    if (isError) {
      setTimeout(() => {
        goError()
      }, 0)
    }
  }, [isError])

  const onDone = () => {
    setUserStore(tempStore)
    navigate('/plannerselection')
  }

  const goBack = () => window.history.back()
  const goError = () => navigate('/error')

  const storeOptions = stores
    .filter(
      (store) =>
        store.storeName.toLowerCase().includes(inputValue.toLowerCase()) ||
        store.storeCode.toLowerCase().includes(inputValue.toLowerCase()),
    )
    .map((store) => ({
      label: `${store.storeCode} ${store.storeName}`,
      value: String(store.storeId),
    }))

  return (
    <Container className='c-common-full-page-center'>
      <Flex
        direction='column'
        justifyContent='center'
        gap='lg'
        style={{ minHeight: '100vh' }}
      >
        <SainsburysOrange
          aria-label='Sainsburys Group plc'
          width={320}
          height={108}
          style={{ marginBottom: '-58px' }}
        />
        <Display6 className='c-title' style={{ margin: 0 }}>
          Production Planning
        </Display6>
        {isLoading && <Loading />}

        {!isLoading && (
          <Flex
            direction='column'
            alignItems='center'
            gap='md'
            style={{ width: '100%', maxWidth: 400 }}
          >
            <div className='c-fable-search-container'>
              <SearchInput
                testid='store-search-input'
                name='store-search-input'
                label='Search for your store...'
                hasHiddenLabel
                button={false}
                leadingIcon
                placeholder='Search for your store...'
                value={inputValue}
                onChange={(value: string) => {
                  setInputValue(value)
                  setIsOpen(true)
                  if (value === '') {
                    setConfirmEnabled(false)
                    setTempStore({
                      storeId: 0,
                      storeCode: '',
                      storeName: '',
                    })
                  }
                }}
                options={storeOptions}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                isLoading={isLoading}
                onSelection={(option) => {
                  if (option && 'value' in option) {
                    const selectedStore = stores.find(
                      (s) => String(s.storeId) === option.value,
                    )
                    if (selectedStore) {
                      setTempStore(selectedStore)
                      setInputValue(option.label)
                      setIsOpen(false)
                      setConfirmEnabled(true)
                    }
                  }
                }}
                isClearable
                onClear={() => {
                  setInputValue('')
                  setTempStore({
                    storeId: 0,
                    storeCode: '',
                    storeName: '',
                  })
                  setConfirmEnabled(false)
                }}
                style={{ width: '100%', maxWidth: 400 }}
              />
            </div>

            <ButtonGroup gap='lg' align='center' orientation='vertical'>
              <Button
                isFullWidth
                disabled={!confirmEnabled}
                onClick={onDone}
                testid='store-save-button'
              >
                Save
              </Button>
              <Button isFullWidth onClick={goBack} testid='store-cancel-button'>
                Cancel
              </Button>
            </ButtonGroup>
          </Flex>
        )}
      </Flex>
    </Container>
  )
}

export default StoreSetting
